import { runAfterLoad, useGsap } from './gsap-lazy';

runAfterLoad(async () => {
  const { gsap, ScrollTrigger } = await useGsap();
  Promise.all([
    import('three'),
    import('three/examples/jsm/loaders/GLTFLoader.js'),
    import('three/examples/jsm/loaders/KTX2Loader.js'),
    import('three/examples/jsm/libs/meshopt_decoder.module.js'),
  ]).then(([THREE, { GLTFLoader }, { KTX2Loader }, { MeshoptDecoder }]) => {

// HELPER: Gerätetyp ermitteln (für responsive Größen)
function getDeviceType() {
  const screenWidth = window.innerWidth;
  if (screenWidth >= 1024) return 'desktop';
  else if (screenWidth >= 768 && screenWidth < 1024) return 'tablet';
  else return 'smartphone';
}
let deviceType = getDeviceType();

// ==========================================================================
// 1. shark CANVAS – Immer laden und normale Animation abspielen
// ==========================================================================

// -----------------------------------------------------------------------
// shark CANVAS – Setup bleibt weitgehend unverändert
// -----------------------------------------------------------------------
const sharkContainer = document.getElementById('shark-container');
const sharkScene = new THREE.Scene();
const sharkCamera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 10000);
sharkCamera.position.set(0, 0, 500);

const sharkRenderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
sharkRenderer.shadowMap.enabled = true;
sharkRenderer.shadowMap.type = THREE.PCFShadowMap;
sharkRenderer.setClearAlpha(0);

// Lights für shark-Szene
const sharkPointLight = new THREE.DirectionalLight(0xffffff, 5);
sharkPointLight.position.set(100, 100, 100);
sharkScene.add(sharkPointLight);
const sharkAmbientLight = new THREE.HemisphereLight(0xffffff, 0xffffff, 2);
sharkScene.add(sharkAmbientLight);

function createConfiguredGLTFLoader(renderer) {
  const gltfLoader = new GLTFLoader();
  const transcoderPath = `${import.meta.env.BASE_URL}basis/`;
  const ktx2Loader = new KTX2Loader().setTranscoderPath(transcoderPath).detectSupport(renderer);
  gltfLoader.setKTX2Loader(ktx2Loader);
  gltfLoader.setMeshoptDecoder(MeshoptDecoder);
  return gltfLoader;
}

// shark Modell laden
const sharkURL = new URL('shark.glb', import.meta.url);
const sharkLoader = createConfiguredGLTFLoader(sharkRenderer);
let sharkMixer, sharkModel;
const sharkBaseRotation = { x: 0.2, y: -0.4 };
const sharkDragState = {
  isDragging: false,
  pointerId: null,
  lastX: 0,
  lastY: 0,
  yawOffset: 0,
  pitchOffset: 0,
};

function applySharkRotation() {
  if (!sharkModel) return;
  sharkModel.rotation.x = sharkBaseRotation.x + sharkDragState.pitchOffset;
  sharkModel.rotation.y = sharkBaseRotation.y + sharkDragState.yawOffset;
}

sharkLoader.load(
  sharkURL.href,
  function (gltf) {
    sharkModel = gltf.scene;
    resizesharkRenderer();
    applySharkRotation();
    sharkScene.add(sharkModel);
    sharkMixer = new THREE.AnimationMixer(sharkModel);
    gltf.animations.forEach((clip) => {
      const action = sharkMixer.clipAction(clip);
      action.play();
    });
  },
  undefined,
  function (error) {
    console.error('Error loading shark model:', error);
  }
);

function resizesharkRenderer() {
  let width, height;
  if (deviceType === 'desktop') {
    width = window.innerWidth / 3;
    height = (3 * window.innerHeight) / 4;
    if(sharkModel) {
      sharkModel.scale.set(0.6, 0.6, 0.6);
      sharkModel.position.set(0.5, 100, 0);
    }
  } else {
    width = window.innerWidth;
    height = window.innerHeight / 2;
    if(sharkModel) {
      sharkModel.scale.set(0.8, 0.8, 0.8);
      sharkModel.position.set(1.5, 100, 0);
    }
  }
  sharkRenderer.setSize(width, height);
  sharkCamera.aspect = width / height;
  sharkCamera.updateProjectionMatrix();
}
resizesharkRenderer();
sharkContainer.appendChild(sharkRenderer.domElement);
sharkRenderer.domElement.style.touchAction = 'none';
sharkRenderer.domElement.style.cursor = 'grab';

const sharkHint = document.createElement('p');
sharkHint.className = 'shark-rotate-hint';
sharkHint.textContent = 'Zum Drehen ziehen (Maus oder Finger)';
sharkContainer.appendChild(sharkHint);

let sharkHintHidden = false;
function hideSharkHint() {
  if (sharkHintHidden) return;
  sharkHintHidden = true;
  sharkHint.classList.add('is-hidden');
}

function startSharkDrag(event) {
  if (!sharkModel) return;
  hideSharkHint();
  sharkDragState.isDragging = true;
  sharkDragState.pointerId = event.pointerId;
  sharkDragState.lastX = event.clientX;
  sharkDragState.lastY = event.clientY;
  sharkRenderer.domElement.setPointerCapture(event.pointerId);
  sharkRenderer.domElement.style.cursor = 'grabbing';
}

function moveSharkDrag(event) {
  if (!sharkDragState.isDragging || event.pointerId !== sharkDragState.pointerId || !sharkModel) return;

  const deltaX = event.clientX - sharkDragState.lastX;
  const deltaY = event.clientY - sharkDragState.lastY;
  sharkDragState.lastX = event.clientX;
  sharkDragState.lastY = event.clientY;

  sharkDragState.yawOffset += deltaX * 0.005;
  sharkDragState.pitchOffset += deltaY * 0.003;
  sharkDragState.pitchOffset = Math.min(0.9, Math.max(-0.9, sharkDragState.pitchOffset));

  applySharkRotation();
}

function endSharkDrag(event) {
  if (!sharkDragState.isDragging) return;
  if (typeof event.pointerId === 'number' && event.pointerId !== sharkDragState.pointerId) return;

  if (
    typeof sharkDragState.pointerId === 'number' &&
    sharkRenderer.domElement.hasPointerCapture(sharkDragState.pointerId)
  ) {
    sharkRenderer.domElement.releasePointerCapture(sharkDragState.pointerId);
  }

  sharkDragState.isDragging = false;
  sharkDragState.pointerId = null;
  sharkRenderer.domElement.style.cursor = 'grab';
}

sharkRenderer.domElement.addEventListener('pointerdown', startSharkDrag);
sharkRenderer.domElement.addEventListener('pointermove', moveSharkDrag);
sharkRenderer.domElement.addEventListener('pointerup', endSharkDrag);
sharkRenderer.domElement.addEventListener('pointercancel', endSharkDrag);
window.setTimeout(hideSharkHint, 7000);

// shark Animation Loop
const sharkClock = new THREE.Clock();
function sharkAnimate() {
  const delta = sharkClock.getDelta();
  if (sharkMixer) sharkMixer.update(delta);
  sharkRenderer.render(sharkScene, sharkCamera);
}
sharkRenderer.setAnimationLoop(sharkAnimate);

// ==========================================================================
// 2. HELIKOPTER CANVAS – Lazy-Loading + Scrollbasierte Positionsänderungen
// ==========================================================================

// ArrPositionModel: Definiert Zielpositionen und Drehungen für den Helikopter
const arrPositionModel = [
  {
    id: 'intro',
    position: { x: 40, y: 5, z: -10 },
    rotation: { x: 1, y: -1, z: 1 }
  },
  {
    id: 'vorteile',
    position: { x: 5, y: 2, z: -5 },
    rotation: { x: 0, y: -1, z: -0.1 }
  },
  {
    id: 'interaktiv',
    position: { x: -7, y: 3, z: -4 },
    rotation: { x: 0.05, y: 0.5, z: -0.3 }
  },
  {
    id: 'scrolling',
    position: { x: 7, y: 4, z: 0 },
    rotation: { x: -0.5, y: 0.5, z: 0 }
  },
  {
    id: 'kreativ',
    position: { x: -5, y: 1, z: -5 },
    rotation: { x: -0.3, y: 0.5, z: -0.5 }
  },
  {
    id: 'ende',
    position: { x: 50, y: 5, z: -15 },
    rotation: { x: 0.5, y: 1, z: -1 }
  },
];

const heliContainer = document.getElementById('container-helicopter');
const heliScene = new THREE.Scene();
const heliCamera = new THREE.PerspectiveCamera(65, window.innerWidth / window.innerHeight, 0.1, 10000);
heliCamera.position.set(0, 7, 10);

const heliRenderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
heliRenderer.setPixelRatio(window.devicePixelRatio);
heliRenderer.shadowMap.enabled = true;
heliRenderer.setClearAlpha(0);

let helicopterModel = null;
const heliBaseTransform = {
  position: { ...arrPositionModel[0].position },
  rotation: { ...arrPositionModel[0].rotation },
};
const heliDrift = {
  phasePosX: Math.random() * Math.PI * 2,
  phasePosY: Math.random() * Math.PI * 2,
  phasePosZ: Math.random() * Math.PI * 2,
  phaseRotX: Math.random() * Math.PI * 2,
  phaseRotY: Math.random() * Math.PI * 2,
  phaseRotZ: Math.random() * Math.PI * 2,
};
let heliDriftTime = Math.random() * 20;

function applyHelicopterDrift(time) {
  if (!helicopterModel) return;

  const driftX = Math.sin(time * 0.7 + heliDrift.phasePosX) * 0.35;
  const driftY = Math.sin(time * 1.05 + heliDrift.phasePosY) * 0.45 + Math.sin(time * 2.1 + heliDrift.phasePosY) * 0.12;
  const driftZ = Math.sin(time * 0.9 + heliDrift.phasePosZ) * 0.22;

  const driftRotX = Math.sin(time * 0.85 + heliDrift.phaseRotX) * 0.04;
  const driftRotY = Math.sin(time * 0.6 + heliDrift.phaseRotY) * 0.03;
  const driftRotZ = Math.sin(time * 1.2 + heliDrift.phaseRotZ) * 0.06;

  helicopterModel.position.set(
    heliBaseTransform.position.x + driftX,
    heliBaseTransform.position.y + driftY,
    heliBaseTransform.position.z + driftZ
  );
  helicopterModel.rotation.set(
    heliBaseTransform.rotation.x + driftRotX,
    heliBaseTransform.rotation.y + driftRotY,
    heliBaseTransform.rotation.z + driftRotZ
  );
}

function resizeHeliRenderer() {
  let width = window.innerWidth;
  let height = window.innerHeight;
  heliRenderer.setSize(width, height);
  if(deviceType === 'desktop') {
    heliCamera.fov = 65;
    heliCamera.position.z = 10;
    if (helicopterModel) {
      helicopterModel.scale.set(1, 1, 1);
    }
  } else {
    heliCamera.fov = 60;
    heliCamera.position.z = 60;
    if (helicopterModel) {
      helicopterModel.scale.set(2.5, 2.5, 2.5);
    }
  }

  heliCamera.aspect = width / height;
  heliCamera.updateProjectionMatrix();
}
heliContainer.appendChild(heliRenderer.domElement);

// Lights für Helikopter-Szene
const heliDirectionalLight = new THREE.DirectionalLight(0xffffff, 30);
heliDirectionalLight.position.set(150, 150, 100);
heliScene.add(heliDirectionalLight);
const heliHemisphereLight = new THREE.HemisphereLight(0xffffff, 0xffffff, 50);
heliScene.add(heliHemisphereLight);

let helicopterLoaded = false;

// Funktion zum Lazy-Loaden des Helikopters
function loadHelicopter() {
  if (helicopterLoaded) return;
  helicopterLoaded = true;
  const heliLoader = createConfiguredGLTFLoader(heliRenderer);
  const heliURL = new URL('helicopter.glb', import.meta.url);
  heliLoader.load(
    heliURL.href,
    (gltf) => {
      helicopterModel = gltf.scene;
      helicopterModel.scale.set(1, 1, 1);
      // Startposition – außerhalb des rechten Bildschirmrands
      Object.assign(heliBaseTransform.position, arrPositionModel[0].position);
      Object.assign(heliBaseTransform.rotation, arrPositionModel[0].rotation);
      applyHelicopterDrift(heliDriftTime);
      heliScene.add(helicopterModel);
      // Eigene Animationen (z. B. Rotoren) abspielen
      const heliMixer = new THREE.AnimationMixer(helicopterModel);
      gltf.animations.forEach((clip) => {
        const action = heliMixer.clipAction(clip);
        action.play();
      });
      helicopterModel.userData.mixer = heliMixer;
      // Nachdem der Helikopter geladen wurde, werden die ScrollTrigger eingerichtet:
      setupHelicopterScrollTriggers();
      resizeHeliRenderer();
    },
    undefined,
    (error) => {
      console.error('Error loading helicopter model:', error);
    }
  );
}

// GSAP ScrollTrigger zum Lazy-Loaden des Helikopters (Trigger: #intro)
ScrollTrigger.create({
  trigger: "#intro",
  start: "top bottom",
  once: true,
  onEnter: loadHelicopter
});

// Funktion zur Animation des Helikopters zu einer Zielposition
function animateHelicopterTo(targetPos) {
  if (!helicopterModel) return;

  gsap.to(heliBaseTransform.position, { 
    x: targetPos.position.x, 
    y: targetPos.position.y, 
    z: targetPos.position.z, 
    duration: 3, 
    overwrite: true,
    ease: "power1.out"
  });
  gsap.to(heliBaseTransform.rotation, { 
    x: targetPos.rotation.x, 
    y: targetPos.rotation.y, 
    z: targetPos.rotation.z, 
    duration: 3, 
    overwrite: true,
    ease: "power1.out"
  });
}

// Funktion zur Einrichtung von ScrollTriggern für jedes Section-Item
function setupHelicopterScrollTriggers() {
  const sections = document.querySelectorAll('.animation-point');
  sections.forEach(section => {
    // Finde das passende Ziel basierend auf der ID des Section-Items
    const targetPos = arrPositionModel.find(item => item.id === section.id);
    if (targetPos) {
      ScrollTrigger.create({
        trigger: section,
        start: "top 70%",   // Trigger, wenn die Section den mittleren Bereich erreicht
        end: "bottom 70%",
        onEnter: () => animateHelicopterTo(targetPos),
        onEnterBack: () => animateHelicopterTo(targetPos),
      });
    }
  });
}

// Helikopter-Animationsloop
const heliClock = new THREE.Clock();
function animateHelicopter() {
  const delta = heliClock.getDelta();
  heliDriftTime += delta;
  if (helicopterModel) {
    if (helicopterModel.userData.mixer) {
      helicopterModel.userData.mixer.update(delta);
    }
    applyHelicopterDrift(heliDriftTime);
  }
  heliRenderer.render(heliScene, heliCamera);
  requestAnimationFrame(animateHelicopter);
}
animateHelicopter();

// ==========================================================================
// GLOBAL RESIZE: Beide Renderer aktualisieren
window.addEventListener('resize', () => {
  deviceType = getDeviceType();
  resizesharkRenderer();
  resizeHeliRenderer();
});

// -----------------------------------------------------------------------
// Zusätzliche UI-Funktionen (Parallax, Input-Handling, Akkordeon)
// -----------------------------------------------------------------------
const inputs = document.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"], textarea');
inputs.forEach(input => {
  input.addEventListener('focus', () => {
    const label = input.previousElementSibling;
    label.style.transform = 'translate(0px, 0px)';
    label.style.color = '#FFFFFF';
  });
  input.addEventListener('blur', () => {
    if (!input.value) {
      const label = input.previousElementSibling;
      label.style.transform = 'translate(10px, 36px)';
      label.style.color = '#FFFFFF';
    }
  });
});
inputs.forEach(input => {
  const label = input.previousElementSibling;
  if (input.value) {
    label.style.transform = 'translate(0px, 0px)';
    label.style.color = '#FFFFFF';
  }
});
}).catch(console.error);
});
